import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
// import images
import bannerImg from "../../image/blog/professional-woman-working-night.jpg";
// import data
import contentData from "../../data/content/blog/blogContent.json";
import { DataKeys, DataStructure, DataFlat } from "../../types";
// import components
import { Col3ContentFrame, MainFrame, SpacingFrame } from "../display/Frame";
import { BannerSection } from "../display/Sections";
import { TextBody, TextBulletList, TitleSubtitle, TextNumberedList } from "../display/TextFormat";
import { BlogCard } from "../display/Cards";
import { UnitBadge } from "../display/Badges";
import { MoreButton, PageControl } from "../tools/Buttons";
import { NavTree } from "../navigation/NavLinkPages";
import { ShowMorePosts } from "../content/BlogNews";
import NoContent from "../content/NoContent";
// import functions
import { scrollTop } from "../../functions/DisplayFunctions";
import { filterDictionaryByKeys, subsetListByChunk } from "../../functions/DataFunctions";
// import context
import { useLang } from "../Context";
// import global var
import { typedVocab } from "../GlobalVar";


const typedContent : DataStructure = contentData;
const imagePath = "image/blog/";


export default function Blog() {
    const { lang } = useLang();
    const content = typedContent["content"];
    const contentKeys = Object.keys(content);

    const [selPage, setSelPage] = useState<number>(1);
    const [contentData, setContentData] = useState<DataStructure>(content);

    useEffect(() => {
        scrollTop();
        let subList = subsetListByChunk(contentKeys, selPage, 9);
        let data = filterDictionaryByKeys(subList, content);
        setContentData(data);
    }, [selPage]);


    let posts = Object.keys(contentData).map((i) => {
        let data = content[i];
        let link = "/blog/" + i;
        return (
            <BlogCard data={data} imagePath={imagePath} link={link} />
        )
    });

    return (
        <>
            <DefaultBanner />
            <NavTree />

            <MainFrame>
                <SpacingFrame>
                    <TitleSubtitle title={ typedVocab["Latest Posts"][lang] } />
                    <Col3ContentFrame>
                        { posts }
                    </Col3ContentFrame>

                    <PageControl contentListLen={contentKeys.length} divider={9} sendSelPage={setSelPage} />
                </SpacingFrame>
            </MainFrame>
        </>
    )
};


export function BlogSubpages() {
    const { lang } = useLang();
    const { blogPosts } = useParams();

    const data = blogPosts
        ? (typedContent["content"][blogPosts] as DataStructure | undefined)
        : undefined;

    if (!data) {
        return <NoContent />;
    }

    const navTitle = data["title"][lang] as string | undefined;

    return (
        <>
            <DefaultBanner />
            {navTitle && <NavTree addLink={navTitle} />}
            <BlogPostsContent data={data} link={blogPosts} />
        </>
    );
}


//////////////////////
// LOCAL FUNCTIONS //
/////////////////////
function DefaultBanner() {
    const { lang } = useLang();
    return (
        <BannerSection image={bannerImg} title={ typedContent["title"][lang] } subtitle={ typedContent["subtitle"][lang] } />
    )
};

interface BlogPostsContentProps {
    data: DataKeys;
    link: string | undefined;
};
function BlogPostsContent({ data, link } : BlogPostsContentProps) {
    const { lang } = useLang();

    const title = data["title"][lang] as string;
    const author = data["author"][lang] as string;
    const date = data["date"][lang] as string;
    const tags = data["tags"] as string[];
    const contentData = data["content"] as DataStructure;
    const buttonData = data["button"] as DataFlat;
    const showButton = Boolean(buttonData?.["label"]);
    const imageName =
        data["image"] !== ""
            ? require("../../" + imagePath + data["image"])
            : undefined;

    let showTags = tags.map((tag) => (
        <UnitBadge key={tag} label={tag} />
    ));

    let content = Object.keys(contentData).map((i) => {
        let textData = contentData[i];
        let isBullet = textData["bullet"];
        let isNumber = textData["number"];
        let title = textData["title"][lang];
        let text = textData["text"][lang];

        if (isBullet) {
            return (
                <TextBulletList subtitle={title} list={text} />
            )
        } else if (isNumber) {
            return (
                <TextNumberedList title={title} text={text} />
            )
        } else {
            return (
                <>
                    { title && <p className="text-left font-bold text-grey-800 -mb-5">{title}</p> }
                    <TextBody text={text} />
                </>
            )
        };
    });

    return (
        <MainFrame>
            <SpacingFrame spacing={128}>
                <SpacingFrame>
                    <div className="w-fit flex items-start justify-start gap-2">
                        { showTags }
                    </div>
                    <div className="flex flex-col">
                        <TitleSubtitle title={ title } left />
                        <p className="text-left text-grey-400">
                            <span>{ author } | </span>
                            <span>{ date }</span>
                        </p>
                    </div>
                    <SpacingFrame spacing={24}>
                        { imageName && <img className="w-full max-h-[600px] object-cover bg-center flex-shrink-0" src={imageName} /> }
                        { content }
                        { showButton && <MoreButton label={ buttonData["label"] } link={ buttonData["link"] } newTab /> }
                    </SpacingFrame>
                </SpacingFrame>

                <ShowMorePosts currPost={link} type="blog" />
            </SpacingFrame>
        </MainFrame>
    )
};